import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { api, resolveMediaUrl } from '../utils/api';
import { SocialLinksRow } from '../components/salon-browser';

export default function ShopDetail() {
  const { shopId } = useParams();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showAlert } = useNotification();

  const [shop, setShop] = useState(null);
  const [branches, setBranches] = useState([]);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [shopData, branchData] = await Promise.all([
          api.getShop(shopId),
          api.getBranches(null, parseInt(shopId)),
        ]);
        setShop(shopData);
        setBranches(branchData);

        if (user && shopData.has_premium) {
          try {
            const memberships = await api.getMyMemberships();
            const match = memberships.find(
              (m) => m.shop_id === parseInt(shopId)
            );
            setMembership(match || null);
          } catch {
            setMembership(null);
          }
        }
      } catch (err) {
        console.error('Failed to load shop:', err);
        showAlert(t('common.error'), 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [shopId, user]);

  const handleRequestMembership = async () => {
    setRequesting(true);
    try {
      await api.requestPremiumMembership(shopId);
      showAlert(t('premium.requestSent') || 'VIP membership requested!', 'success');
      // Refresh memberships
      try {
        const memberships = await api.getMyMemberships();
        const match = memberships.find((m) => m.shop_id === parseInt(shopId));
        setMembership(match || null);
      } catch {
        setMembership({ status: 'pending_approval' });
      }
    } catch (err) {
      showAlert(err.message || t('common.error'), 'error');
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', color: 'var(--text-muted)', padding: '7rem 1.5rem 5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {t('common.loading')}
      </div>
    );
  }

  if (!shop) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-base)', color: 'var(--text-muted)', padding: '7rem 1.5rem 5rem', textAlign: 'center' }}>
        <p>{t('shops.noSalonsFound') || 'Shop not found'}</p>
      </div>
    );
  }

  const vipFeatures = [
    { icon: '⏰', label: 'Priority Window' },
    { icon: '✨', label: 'Preferred Artist' },
    { icon: '📍', label: 'On My Way ETA' },
    { icon: '🤝', label: 'Handoff Protocol' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', color: '#ffffff', padding: '7rem 1.5rem 5rem', fontFamily: "'Roboto', sans-serif" }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>

        {/* ── HERO ── */}
        <div
          style={{
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            minHeight: '280px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            background: shop.logo_url
              ? `linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.92) 100%), url(${resolveMediaUrl(shop.logo_url)}) center/cover no-repeat`
              : 'linear-gradient(135deg, var(--bg-card) 0%, var(--bg-base) 100%)',
            marginBottom: '2rem',
          }}
        >
          <div style={{ padding: '2rem 1.5rem', position: 'relative', zIndex: 1 }}>
            {/* Badges row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
              <span style={{ padding: '0.25rem 0.7rem', borderRadius: '6px', background: 'var(--orange)', color: '#fff', fontSize: '0.7rem', fontWeight: 700, fontFamily: "'Oswald', sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                {shop.category}
              </span>
              <span style={{ padding: '0.25rem 0.7rem', borderRadius: '6px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(6px)', color: '#fff', fontSize: '0.75rem', fontWeight: 700 }}>
                ★ {shop.rating != null ? shop.rating : '—'}
              </span>
              {shop.has_premium && (
                <span style={{ padding: '0.25rem 0.7rem', borderRadius: '6px', background: '#D4AF37', color: '#000', fontSize: '0.7rem', fontWeight: 700, fontFamily: "'Oswald', sans-serif", letterSpacing: '0.1em' }}>
                  VIP
                </span>
              )}
            </div>
            <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(2rem, 5vw, 3.5rem)', letterSpacing: '0.06em', color: '#fff', lineHeight: 1.1, margin: 0 }}>
              {shop.name}
            </h1>
            {shop.locations && shop.locations.length > 0 && (
              <p style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.85)', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                <span aria-hidden="true">📍</span>
                <span>{shop.locations.join(' · ')}</span>
              </p>
            )}
            <div style={{ marginTop: '0.75rem' }}>
              <SocialLinksRow social={shop.social_links} />
            </div>
          </div>
        </div>

        {/* ── VIP SECTION ── */}
        {shop.has_premium && (
          <div
            style={{
              border: '2px solid #D4AF37',
              borderRadius: '14px',
              padding: '1.75rem',
              marginBottom: '2.5rem',
              background: 'var(--bg-card)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
              <span style={{ fontSize: '1.5rem' }} aria-hidden="true">👑</span>
              <h2 style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1.3rem', fontWeight: 700, letterSpacing: '0.06em', color: '#D4AF37', margin: 0 }}>
                VIP Premium Available
              </h2>
            </div>

            {shop.premium_monthly_price != null && (
              <p style={{ fontSize: '1.6rem', fontWeight: 700, color: '#D4AF37', marginBottom: '1.25rem' }}>
                {shop.premium_monthly_price} <span style={{ fontSize: '0.85rem', fontWeight: 400, color: 'var(--text-muted)' }}>/ month</span>
              </p>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {vipFeatures.map((f) => (
                <div
                  key={f.label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.6rem 0.8rem',
                    borderRadius: '8px',
                    background: 'var(--bg-base)',
                    border: '1px solid var(--border)',
                    fontSize: '0.85rem',
                    color: '#ffffff',
                  }}
                >
                  <span style={{ fontSize: '1.1rem' }}>{f.icon}</span>
                  {f.label}
                </div>
              ))}
            </div>

            {/* Action based on membership status */}
            {!user ? (
              <Link
                to="/login"
                style={{
                  display: 'inline-block',
                  padding: '0.75rem 2rem',
                  borderRadius: '9999px',
                  background: '#D4AF37',
                  color: '#000',
                  fontFamily: "'Oswald', sans-serif",
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                {t('auth.loginToJoinVIP') || 'Login to Join VIP'}
              </Link>
            ) : !membership ? (
              <button
                onClick={handleRequestMembership}
                disabled={requesting}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '9999px',
                  background: requesting ? 'var(--text-muted)' : '#D4AF37',
                  color: '#000',
                  border: 'none',
                  fontFamily: "'Oswald', sans-serif",
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  cursor: requesting ? 'not-allowed' : 'pointer',
                  opacity: requesting ? 0.7 : 1,
                }}
              >
                {requesting ? (t('common.loading') || 'Loading...') : 'Request VIP Membership'}
              </button>
            ) : membership.status === 'pending_approval' ? (
              <span
                style={{
                  display: 'inline-block',
                  padding: '0.75rem 2rem',
                  borderRadius: '9999px',
                  background: 'var(--bg-base)',
                  border: '2px solid #D4AF37',
                  color: '#D4AF37',
                  fontFamily: "'Oswald', sans-serif",
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  opacity: 0.8,
                }}
              >
                Request Pending
              </span>
            ) : membership.status === 'active' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '0.6rem 1.5rem',
                    borderRadius: '9999px',
                    background: '#D4AF37',
                    color: '#000',
                    fontFamily: "'Oswald', sans-serif",
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  You are a VIP Member
                </span>
                <Link
                  to="/premium"
                  style={{
                    color: '#D4AF37',
                    fontSize: '0.88rem',
                    fontWeight: 600,
                    textDecoration: 'underline',
                  }}
                >
                  {t('premium.goToPremium') || 'Go to Premium'}
                </Link>
              </div>
            ) : (
              <button
                onClick={handleRequestMembership}
                disabled={requesting}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '9999px',
                  background: requesting ? 'var(--text-muted)' : '#D4AF37',
                  color: '#000',
                  border: 'none',
                  fontFamily: "'Oswald', sans-serif",
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  cursor: requesting ? 'not-allowed' : 'pointer',
                  opacity: requesting ? 0.7 : 1,
                }}
              >
                {requesting ? (t('common.loading') || 'Loading...') : 'Request VIP Membership'}
              </button>
            )}
          </div>
        )}

        {/* ── BRANCHES ── */}
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1.4rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#ffffff', marginBottom: '1.25rem' }}>
            {t('branches.title') || 'Branches'}
          </h2>

          {branches.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              {t('branches.noBranches') || 'No branches found.'}
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1.25rem' }}>
              {branches.map((branch) => (
                <div
                  key={branch.id}
                  className="barber-card"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: branch.logo_url ? 'minmax(96px, 120px) 1fr' : '1fr',
                    gap: '1rem',
                    padding: '1rem',
                    borderRadius: '12px',
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    color: '#ffffff',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    alignItems: 'stretch',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--orange)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(255,107,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div
                    style={{
                      borderRadius: '8px',
                      overflow: 'hidden',
                      background: 'var(--bg-dark)',
                      border: '1px solid var(--border)',
                      minHeight: 88,
                      aspectRatio: branch.logo_url ? '1 / 1' : undefined,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {branch.logo_url ? (
                      <img
                        src={resolveMediaUrl(branch.logo_url)}
                        alt=""
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <span style={{ fontSize: '1.75rem', opacity: 0.45 }} aria-hidden="true">📍</span>
                    )}
                  </div>
                  <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <h3 style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1.05rem', fontWeight: 700, letterSpacing: '0.04em', marginBottom: '0.4rem', color: '#ffffff' }}>
                      {branch.name}
                    </h3>
                    {branch.address && (
                      <p style={{ fontSize: '0.82rem', color: 'rgba(255, 255, 255, 0.88)', margin: 0, lineHeight: 1.4 }}>
                        {branch.address}
                      </p>
                    )}
                    {branch.working_hours && (
                      <p style={{ fontSize: '0.78rem', color: '#ffffff', margin: '0.35rem 0 0' }}>
                        {(t('shops.workingHours') || 'Working hours')}: {branch.working_hours}
                      </p>
                    )}
                    <SocialLinksRow social={branch.manager_social_links} size="small" />
                    <Link
                      to={`/booking?branch_id=${branch.id}`}
                      className="branch-card-book-btn"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: '0.65rem',
                        padding: '0.65rem 1rem',
                        borderRadius: '9999px',
                        background: 'var(--orange)',
                        color: '#fff',
                        fontFamily: "'Oswald', sans-serif",
                        fontSize: '0.82rem',
                        fontWeight: 600,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        textDecoration: 'none',
                      }}
                    >
                      {t('shops.bookNow') || 'Book Now'}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
